import type { OperatorDailyRow, RouteDailyRow } from '../../../services/reports.service';
import { CPD_COLORS } from '../dashboard/dashboard.charts';
import { fmtSecs, num, pct } from './reports.types';

type Row = OperatorDailyRow | RouteDailyRow;

/** Tabla de cierre diario por operador o por ruta (GET reports/operators | reports/routes). */
export default function DailyTable({
  rows,
  keyLabel,
  keyOf
}: {
  rows: Row[];
  keyLabel: string;
  keyOf: (r: Row) => string;
}) {
  const sorted = [...rows].sort((a, b) =>
    a.day < b.day ? 1 : a.day > b.day ? -1 : keyOf(a).localeCompare(keyOf(b))
  );
  const tot = rows.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      answered: acc.answered + r.answered,
      human: acc.human + r.human,
      voicemail: acc.voicemail + r.voicemail,
      unknown: acc.unknown + r.unknown,
      billsecSum: acc.billsecSum + r.billsecSum
    }),
    { total: 0, answered: 0, human: 0, voicemail: 0, unknown: 0, billsecSum: 0 }
  );
  const rate = (v: number, h: number) => (v + h ? pct((v / (v + h)) * 100) : '—');

  return (
    <div className="table-responsive">
      <table className="table table-row-dashed align-middle fs-7 gy-2">
        <thead>
          <tr className="text-muted fw-bold text-uppercase fs-8">
            <th>Día</th>
            <th>{keyLabel}</th>
            <th className="text-end">Llamadas</th>
            <th className="text-end">Contestadas</th>
            <th className="text-end" style={{ color: CPD_COLORS.human }}>
              Humano
            </th>
            <th className="text-end" style={{ color: CPD_COLORS.voicemail }}>
              Buzón
            </th>
            <th className="text-end">Sin ver.</th>
            <th className="text-end">% buzón</th>
            <th className="text-end">Billsec</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center text-muted py-8">
                Sin datos en el rango
              </td>
            </tr>
          )}
          {sorted.map((r) => (
            <tr key={`${r.day}-${keyOf(r)}`}>
              <td className="text-nowrap">{r.day}</td>
              <td className="fw-bold text-uppercase">{keyOf(r) || '—'}</td>
              <td className="text-end">{num(r.total)}</td>
              <td className="text-end">{num(r.answered)}</td>
              <td className="text-end">{num(r.human)}</td>
              <td className="text-end">{num(r.voicemail)}</td>
              <td className="text-end text-muted">{num(r.unknown)}</td>
              <td className="text-end fw-bold">{rate(r.voicemail, r.human)}</td>
              <td className="text-end">{fmtSecs(r.billsecSum)}</td>
            </tr>
          ))}
        </tbody>
        {sorted.length > 0 && (
          <tfoot>
            <tr className="fw-bolder border-top">
              <td colSpan={2}>Total</td>
              <td className="text-end">{num(tot.total)}</td>
              <td className="text-end">{num(tot.answered)}</td>
              <td className="text-end">{num(tot.human)}</td>
              <td className="text-end">{num(tot.voicemail)}</td>
              <td className="text-end text-muted">{num(tot.unknown)}</td>
              <td className="text-end">{rate(tot.voicemail, tot.human)}</td>
              <td className="text-end">{fmtSecs(tot.billsecSum)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
